import {
    AlertModal,
    Box,
    Button,
    Checkmark,
    ContextualBanner,
    Copy,
    Modal,
    RadioInput,
    TextArea,
    TextBlock,
    TextInput,
} from "@bcc-ui/ui-components";
import classNames from "classnames";
import * as React from "react";
import { useTranslation } from "react-i18next";

import style from "./share-report-dialog.scss";
import {
    getDefaultPublicLinkExpiration,
    PublicLinkExpirationDatePicker,
} from "components/reports/PublicLinkExpirationDatePicker";
import { reportPublicLinkService } from "services/report/ReportPublicLinkService";
import { Action, Category, usageStatisticsService } from "services/statistics/UsageStatisticsService";
import { formatDatePickerDate, formatIsoDate } from "utils/format";
import { logger } from "utils/logging";

import testIds from "testIds.json";

const FORM_ID = "report-share-form";
const LINK_INPUT_ID = "report-share-link";
const LINK_SEPARATOR_NAME = "report-share-link-separator";

type LinkSeparator = "NEW_LINE" | "COMMA";

const SEPARATOR_CHARACTERS: Record<LinkSeparator, string> = {
    NEW_LINE: "\n",
    COMMA: ", ",
};

const requestPublicLinks = async (
    reportUuids: string[],
    expiration: Date,
    languageCode: string,
    abortController: AbortController
): Promise<string[]> => {
    const request = { expiration: formatIsoDate(expiration), language: languageCode };
    if (reportUuids.length === 1) {
        const response = await reportPublicLinkService.createReportPublicLink(reportUuids[0], request, abortController);
        return [response.link];
    }
    const response = await reportPublicLinkService.createReportPublicLinks(reportUuids, request, abortController);
    if (response.links.length === 0) {
        throw new Error("No public links were created for the selected reports.");
    }
    return response.links.map((each) => each.link);
};

interface Props {
    reportUuids: string[];
    languageCode: string;
    hide: () => void;
}

const ShareReportDialog = (props: Props) => {
    const { t } = useTranslation();
    const [expiration, setExpiration] = React.useState<Date>(getDefaultPublicLinkExpiration);
    const [loading, setLoading] = React.useState(false);
    const [failed, setFailed] = React.useState(false);
    const [links, setLinks] = React.useState<string[]>();
    const [copied, setCopied] = React.useState(false);
    const [separator, setSeparator] = React.useState<LinkSeparator>("COMMA");
    const abortControllerRef = React.useRef<AbortController>();
    const reportCount = props.reportUuids.length;

    React.useEffect(() => {
        return () => abortControllerRef.current?.abort();
    }, []);

    const createLink = async () => {
        const abortController = new AbortController();
        abortControllerRef.current = abortController;
        setFailed(false);
        setLoading(true);
        usageStatisticsService.sendEvent({
            category: Category.REPORTS,
            action: reportCount === 1 ? Action.SHARE_SINGLE_REPORT : Action.SHARE_SELECTED_REPORTS,
        });
        try {
            const createdLinks = await requestPublicLinks(
                props.reportUuids,
                expiration,
                props.languageCode,
                abortController
            );
            if (!abortController.signal.aborted) {
                setLinks(createdLinks);
            }
        } catch (e) {
            if (!abortController.signal.aborted) {
                logger.error("Failed to create report public links: ", e);
                setFailed(true);
            }
        } finally {
            if (!abortController.signal.aborted) {
                setLoading(false);
            }
        }
    };

    const copyLinks = async (linksToCopy: string) => {
        try {
            await navigator.clipboard.writeText(linksToCopy);
            setCopied(true);
        } catch (e) {
            logger.warn("Failed to copy report public links to clipboard: ", e);
        }
    };

    const selectSeparator = (newSeparator: LinkSeparator) => {
        setSeparator(newSeparator);
        setCopied(false);
    };

    if (links) {
        const count = links.length;
        const joinedLinks = links.join(SEPARATOR_CHARACTERS[separator]);
        const actionButtons = (
            <>
                <Button
                    variant="Primary"
                    icon={copied ? <Checkmark /> : <Copy />}
                    onClick={() => copyLinks(joinedLinks)}
                    onMouseLeave={() => setCopied(false)}
                    onBlur={() => setCopied(false)}
                    testId={testIds.workArea.report.erasure.shareDialog.copyButton}
                >
                    <span className={style.copyButtonLabel}>
                        <span className={classNames(style.copyButtonLabelText, { [style.hidden]: copied })}>
                            {t("ErasureReport.share.copyLink", { count: count })}
                        </span>
                        <span className={classNames(style.copyButtonLabelText, { [style.hidden]: !copied })}>
                            {t("Common.copied")}
                        </span>
                    </span>
                </Button>
                <Button variant="Secondary" onClick={props.hide} testId={testIds.common.dialog.closeButton}>
                    {t("Common.cancel")}
                </Button>
            </>
        );
        return (
            <Modal
                isOpen={true}
                hideModal={props.hide}
                title={t("ErasureReport.share.linkCreatedTitle", { count: count })}
                width={count > 1 ? "Lg" : "Md"}
                buttons={actionButtons}
            >
                <Box columnLayout gap="Md">
                    <TextBlock disableBottomSpacing>
                        {t("ErasureReport.share.linkExpiresOn", { count, date: formatDatePickerDate(expiration) })}
                    </TextBlock>
                    <ContextualBanner variant="Warning">
                        {t("ErasureReport.share.copyWarning", { count: count })}
                    </ContextualBanner>
                    {count > 1 && (
                        <Box gap="Lg" verticalAlignment="Top">
                            <TextBlock disableBottomSpacing>{t("ErasureReport.share.separatorWithColon")}</TextBlock>
                            <Box columnLayout gap="Xs">
                                <RadioInput
                                    id="report-share-separator-new-line"
                                    name={LINK_SEPARATOR_NAME}
                                    label={t("ErasureReport.share.separatorNewLine")}
                                    checked={separator === "NEW_LINE"}
                                    onClick={() => selectSeparator("NEW_LINE")}
                                    testIds={{
                                        input: testIds.workArea.report.erasure.shareDialog.separatorNewLineRadioButton,
                                    }}
                                />
                                <RadioInput
                                    id="report-share-separator-comma"
                                    name={LINK_SEPARATOR_NAME}
                                    label={t("ErasureReport.share.separatorComma")}
                                    checked={separator === "COMMA"}
                                    onClick={() => selectSeparator("COMMA")}
                                    testIds={{
                                        input: testIds.workArea.report.erasure.shareDialog.separatorCommaRadioButton,
                                    }}
                                />
                            </Box>
                        </Box>
                    )}
                    {count > 1 ? (
                        // The component library fixes the textarea width to 320px, so override it to use the modal width.
                        <div className={style.linksTextArea}>
                            <TextArea
                                name={LINK_INPUT_ID}
                                id={LINK_INPUT_ID}
                                label={t("ErasureReport.share.publicLinkWithColon", { count: count })}
                                readOnly
                                value={joinedLinks}
                                testIds={{ textArea: testIds.workArea.report.erasure.shareDialog.linkInput }}
                            />
                        </div>
                    ) : (
                        <TextInput
                            name={LINK_INPUT_ID}
                            id={LINK_INPUT_ID}
                            label={t("ErasureReport.share.publicLinkWithColon", { count: count })}
                            readOnly
                            value={joinedLinks}
                            testIds={{ input: testIds.workArea.report.erasure.shareDialog.linkInput }}
                        />
                    )}
                </Box>
            </Modal>
        );
    }

    if (failed) {
        return (
            <AlertModal
                title={t("ErasureReport.share.failureTitle", { count: reportCount })}
                variant="Error"
                isOpen={true}
                hideModal={() => setFailed(false)}
                buttons={
                    <Button
                        variant="Primary"
                        onClick={() => setFailed(false)}
                        testId={testIds.common.dialog.closeButton}
                    >
                        {t("Common.ok")}
                    </Button>
                }
            >
                {t("ErasureReport.share.failureMessage", { count: reportCount })}
            </AlertModal>
        );
    }

    return (
        <Modal
            isOpen={true}
            hideModal={props.hide}
            title={t("ErasureReport.share.title", { count: reportCount })}
            isLoading={loading}
            buttons={
                <>
                    <Button
                        type="submit"
                        form={FORM_ID}
                        variant="Primary"
                        testId={testIds.workArea.report.erasure.shareDialog.createButton}
                    >
                        {t("ErasureReport.share.createLink", { count: reportCount })}
                    </Button>
                    <Button
                        type="button"
                        variant="Secondary"
                        testId={testIds.common.dialog.closeButton}
                        onClick={props.hide}
                    >
                        {t("Common.cancel")}
                    </Button>
                </>
            }
        >
            <form
                id={FORM_ID}
                onSubmit={(event) => {
                    event.preventDefault();
                    createLink();
                }}
            >
                <TextBlock>{t("ErasureReport.share.description", { count: reportCount })}</TextBlock>
                <PublicLinkExpirationDatePicker
                    name="report-share-expiration"
                    label={t("Common.expirationDateWithColon")}
                    value={expiration}
                    onChange={setExpiration}
                    testId={testIds.workArea.report.erasure.shareDialog.expirationInput}
                />
            </form>
        </Modal>
    );
};

export default ShareReportDialog;
